import * as MutationHelpers from 'shared/helpers/vuex/mutationHelpers';
import types from '../mutation-types';
import CSATReports from '../../api/csatReports';
import { downloadCsvFile } from '../../helper/downloadHelper';
import AnalyticsHelper from '../../helper/AnalyticsHelper';
import { REPORTS_EVENTS } from '../../helper/AnalyticsHelper/events';

let latestResponsesRequest = 0;
let latestMetricsRequest = 0;

const computeDistribution = (value, total) =>
  ((value * 100) / total).toFixed(2);

export const state = {
  records: [],
  metrics: {
    totalResponseCount: 0,
    ratingsCount: {
      1: 0,
      2: 0,
      3: 0,
      4: 0,
      5: 0,
    },
    totalSentMessagesCount: 0,
  },
  uiFlags: {
    isFetching: false,
    isFetchingMetrics: false,
    hasFetchError: false,
    hasMetricsError: false,
  },
};

export const getters = {
  getCSATResponses(_state) {
    return _state.records;
  },
  getMetrics(_state) {
    return _state.metrics;
  },
  getUIFlags(_state) {
    return _state.uiFlags;
  },
  getSatisfactionScore(_state) {
    if (!_state.metrics.totalResponseCount) {
      return 0;
    }
    return computeDistribution(
      _state.metrics.ratingsCount[4] + _state.metrics.ratingsCount[5],
      _state.metrics.totalResponseCount
    );
  },
  getResponseRate(_state) {
    if (!_state.metrics.totalSentMessagesCount) {
      return 0;
    }
    return computeDistribution(
      _state.metrics.totalResponseCount,
      _state.metrics.totalSentMessagesCount
    );
  },
  getRatingCount(_state) {
    return _state.metrics.ratingsCount;
  },
};

export const actions = {
  get: async function getResponses({ commit }, params) {
    latestResponsesRequest += 1;
    const requestId = latestResponsesRequest;
    commit(types.SET_CSAT_RESPONSE_UI_FLAG, {
      isFetching: true,
      hasFetchError: false,
    });
    try {
      const response = await CSATReports.get(params);
      if (requestId === latestResponsesRequest) {
        commit(types.SET_CSAT_RESPONSE, response.data);
      }
    } catch {
      if (requestId === latestResponsesRequest) {
        commit(types.SET_CSAT_RESPONSE_UI_FLAG, { hasFetchError: true });
      }
    } finally {
      if (requestId === latestResponsesRequest) {
        commit(types.SET_CSAT_RESPONSE_UI_FLAG, { isFetching: false });
      }
    }
  },
  getMetrics: async function getMetrics({ commit }, params) {
    latestMetricsRequest += 1;
    const requestId = latestMetricsRequest;
    commit(types.SET_CSAT_RESPONSE_UI_FLAG, {
      isFetchingMetrics: true,
      hasMetricsError: false,
    });
    try {
      const response = await CSATReports.getMetrics(params);
      if (requestId === latestMetricsRequest) {
        commit(types.SET_CSAT_RESPONSE_METRICS, response.data);
      }
    } catch {
      if (requestId === latestMetricsRequest) {
        commit(types.SET_CSAT_RESPONSE_UI_FLAG, { hasMetricsError: true });
      }
    } finally {
      if (requestId === latestMetricsRequest) {
        commit(types.SET_CSAT_RESPONSE_UI_FLAG, { isFetchingMetrics: false });
      }
    }
  },
  downloadCSATReports(_, params) {
    return CSATReports.download(params).then(response => {
      downloadCsvFile(params.fileName, response.data);
      AnalyticsHelper.track(REPORTS_EVENTS.DOWNLOAD_REPORT, {
        reportType: 'csat',
      });
    });
  },
  update: async ({ commit }, { id, reviewNotes }) => {
    const response = await CSATReports.update(id, {
      csat_review_notes: reviewNotes,
    });
    commit(types.UPDATE_CSAT_RESPONSE, response.data);
    return response.data;
  },
};

export const mutations = {
  [types.SET_CSAT_RESPONSE_UI_FLAG](_state, data) {
    _state.uiFlags = {
      ..._state.uiFlags,
      ...data,
    };
  },

  [types.SET_CSAT_RESPONSE]: MutationHelpers.set,
  [types.SET_CSAT_RESPONSE_METRICS](
    _state,
    {
      total_count: totalResponseCount,
      ratings_count: ratingsCount,
      total_sent_messages_count: totalSentMessagesCount,
    }
  ) {
    _state.metrics.totalResponseCount = totalResponseCount || 0;
    _state.metrics.ratingsCount = {
      1: ratingsCount['1'] || 0,
      2: ratingsCount['2'] || 0,
      3: ratingsCount['3'] || 0,
      4: ratingsCount['4'] || 0,
      5: ratingsCount['5'] || 0,
    };
    _state.metrics.totalSentMessagesCount = totalSentMessagesCount || 0;
  },
  [types.UPDATE_CSAT_RESPONSE]: MutationHelpers.update,
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
